import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DocumentsLogicService } from '@app/logic/documents';
import { CbDialogContainerModule } from '@app/shared/components/dialog/dialog-container/dialog-container.module';
import { ImportLotsDialogComponent } from '@app/views/projects/release/lots/summary/import/import-lots-dialog.component';
import { stringifyReadableError } from 'cb-hub-lib';
import { firstValueFrom, take } from 'rxjs';
import { LotMigratorExecutionService } from '../lot-migrator-execution.service';
import {
    IDocumentMigrationPreviewRow,
    IImportLotsPreviewState,
    ILotCancellationPreviewRow,
    ILotMigratorExecutionPreviewDialogData,
    IPreMigrationCheckPreviewRow,
    TExecutionStatus
} from './lot-migrator-execution-preview.models';
import {
    IMigrateLotsWizardDocumentAssignmentDto,
    IMigrateLotsWizardDocumentMigrateResponseDto,
    IMigrateLotsWizardDocumentMigrateResultDto,
    IMigrateLotsWizardHistoryRequestDto,
    IProjectReleaseLotMigrationSourceSelectionGroupDto,
    IProjectReleaseLotMigrationSourceSelectionLotDto
} from '@classictechsolutions/hubapi-transpiled-enums';

interface IDocumentMigrationRequest {
    row: IDocumentMigrationPreviewRow;
    assignment: IMigrateLotsWizardDocumentAssignmentDto;
}

@Component({
    selector: 'cb-lot-migrator-execution-preview-dialog',
    standalone: true,
    imports: [
        CommonModule,
        CbDialogContainerModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule
    ],
    templateUrl: './lot-migrator-execution-preview-dialog.component.html',
    styleUrls: ['./lot-migrator-execution-preview-dialog.component.scss']
})
export class LotMigratorExecutionPreviewDialogComponent implements OnInit {
    public readonly preMigrationRows: IPreMigrationCheckPreviewRow[] = [];
    public readonly cancelRows: ILotCancellationPreviewRow[] = [];
    public readonly documentRows: IDocumentMigrationPreviewRow[] = [];
    public readonly importStep: IImportLotsPreviewState = {
        status: 'pending'
    };
    public showImportStep = false;
    public showDocumentStep = false;
    public documentStepStatus: TExecutionStatus = 'pending';

    constructor(
        public readonly dialogRef: MatDialogRef<LotMigratorExecutionPreviewDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public readonly data: ILotMigratorExecutionPreviewDialogData,
        @Inject(DOCUMENT) private readonly documentRef: Document,
        private readonly dialog: MatDialog,
        private readonly executionService: LotMigratorExecutionService,
        private readonly documentsLogicService: DocumentsLogicService
    ) {}

    public ngOnInit(): void {
        this.dialogRef.disableClose = true;
        this.cancelRows.push(
            ...this.data.lots.map(lot => ({
                id: lot.id,
                lotNumber: lot.lotNumber,
                lot,
                status: 'pending' as const
            }))
        );
        this.preMigrationRows.push(
            ...this.data.lots
                .filter(lot => lot.parentId == null)
                .map(lot => ({
                    id: lot.id,
                    lotNumber: lot.lotNumber,
                    childLots: this.data.lots
                        .filter(childLot => childLot.parentId === lot.id)
                        .sort((a, b) => a.lotNumber - b.lotNumber)
                        .map(childLot => ({
                            id: childLot.id,
                            lotNumber: childLot.lotNumber
                        })),
                    status: 'pending' as const
                }))
        );

        this.documentRows.push(
            ...this.data.docAssignments.map(assignment => ({
                id: `${assignment.documentId}:${assignment.documentEntityId}:${assignment.destinationLotNumber}`,
                documentName: assignment.documentName,
                sourceLotNumber: assignment.sourceLotNumber,
                destinationLotNumber: assignment.destinationLotNumber,
                assignment,
                status: 'pending' as const
            }))
        );

        void this.startExecutionFlow();
    }

    public get canCloseDialog(): boolean {
        return (
            this.preMigrationStepStatus === 'error' ||
            this.cancelStepStatus === 'error' ||
            this.importStep.status === 'error' ||
            this.documentStepStatus === 'success' ||
            this.documentStepStatus === 'error' ||
            this.documentStepStatus === 'skipped'
        );
    }

    public get preMigrationStepStatus(): TExecutionStatus {
        if (this.preMigrationRows.length < 1) {
            return 'skipped';
        }

        if (this.preMigrationRows.some(row => row.status === 'running')) {
            return 'running';
        }

        if (this.preMigrationRows.some(row => row.status === 'error')) {
            return 'error';
        }

        if (this.preMigrationRows.every(row => row.status === 'success')) {
            return 'success';
        }

        return 'pending';
    }

    public get cancelStepStatus(): TExecutionStatus {
        if (this.cancelRows.length < 1) {
            return 'skipped';
        }

        if (this.cancelRows.some(row => row.status === 'running')) {
            return 'running';
        }

        if (this.cancelRows.some(row => row.status === 'error')) {
            return 'error';
        }

        if (this.cancelRows.every(row => row.status === 'success')) {
            return 'success';
        }

        return 'pending';
    }

    public get hasFailedCancellations(): boolean {
        return this.cancelRows.some(row => row.status === 'error');
    }

    public get hasFailedPreMigrationChecks(): boolean {
        return this.preMigrationRows.some(row => row.status === 'error');
    }

    public get allCancellationsSuccessful(): boolean {
        return this.cancelRows.every(row => row.status === 'success');
    }

    public get hasDocumentIssues(): boolean {
        return this.documentRows.some(row => row.status === 'error' || row.status === 'downloaded');
    }

    public get canNotifyAndComplete(): boolean {
        return (
            this.documentStepStatus === 'success' ||
            this.documentStepStatus === 'error' ||
            this.documentStepStatus === 'skipped'
        );
    }

    public get headerMessage(): string {
        if (this.documentStepStatus === 'success' || this.documentStepStatus === 'skipped') {
            return 'Lot migration completed successfully.';
        }

        if (this.documentStepStatus === 'error') {
            return 'Lot migration completed with document issues that still need follow-up.';
        }

        if (this.hasFailedPreMigrationChecks) {
            return 'Pre-migration checks failed. Fix the affected lots, then retry the checks to continue.';
        }

        if (this.importStep.status === 'error') {
            return 'Lot import failed. Download the CSV, correct it, then retry the import.';
        }

        if (this.hasFailedCancellations) {
            return 'Retry the failed lot cancellations before the migration can continue.';
        }

        return 'Executing lot migration steps.';
    }

    public getStatusIcon(status: TExecutionStatus): string {
        switch (status) {
            case 'success':
                return 'check_circle';
            case 'error':
                return 'cancel';
            case 'downloaded':
                return 'check_circle';
            case 'skipped':
                return 'check_circle_outline';
            case 'pending':
                return 'schedule';
            default:
                return 'hourglass_top';
        }
    }

    public getStatusLabel(status: TExecutionStatus): string {
        switch (status) {
            case 'success':
                return 'Complete';
            case 'error':
                return 'Failed';
            case 'downloaded':
                return 'Downloaded';
            case 'running':
                return 'In progress';
            case 'skipped':
                return 'Skipped';
            default:
                return 'Waiting';
        }
    }

    public getStatusClass(status: TExecutionStatus): string {
        return `execution-status--${status}`;
    }

    public retryPreMigrationChecks(): void {
        if (this.preMigrationStepStatus === 'running') {
            return;
        }

        this.startPreMigrationChecks(() => {
            if (this.preMigrationRows.every(row => row.status === 'success')) {
                void this.startCancellationStep();
            }
        });
    }

    public async retryCancellation(row: ILotCancellationPreviewRow): Promise<void> {
        if (row.status === 'running') {
            return;
        }

        const rowIndex = this.cancelRows.findIndex(cancelRow => cancelRow.id === row.id);
        if (rowIndex < 0) {
            return;
        }

        await this.executeCancellationRow(row);

        if (this.allCancellationsSuccessful && !this.showImportStep) {
            await this.startImportStep();
        }
    }

    public downloadCsv(): void {
        const downloadLink = this.documentRef.createElement('a');
        const objectUrl = URL.createObjectURL(this.data.importFile);

        downloadLink.href = objectUrl;
        downloadLink.download = this.data.importFile.name;
        this.documentRef.body.appendChild(downloadLink);
        downloadLink.click();
        this.documentRef.body.removeChild(downloadLink);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    }

    public openImportRetryDialog(): void {
        if (this.importStep.status === 'running') {
            return;
        }

        this.dialog
            .open(ImportLotsDialogComponent, {
                data: {
                    projectReleaseStageMappedItem: this.data.projectReleaseStageMappedItem
                },
                minWidth: '1000px'
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe(result => {
                if (result) {
                    void this.resumeAfterSuccessfulImportRetry();
                }
            });
    }

    public async downloadDocument(row: IDocumentMigrationPreviewRow): Promise<void> {
        try {
            await firstValueFrom(
                this.documentsLogicService.downloadDocument(row.assignment.documentId).pipe(take(1))
            );
            row.status = 'downloaded';
        } catch (error: unknown) {
            row.errorMessage = this.stringifyError(error);
        }
    }

    public notifyAndComplete(): void {
        this.dialogRef.close(true);
    }

    public close(): void {
        if (!this.canCloseDialog) {
            return;
        }

        this.dialogRef.close();
    }

    private startExecutionFlow(): void {
        this.startPreMigrationChecks(() => {
            if (this.preMigrationRows.some(row => row.status !== 'success')) {
                return;
            }

            void this.startCancellationStep();
        });
    }

    private startPreMigrationChecks(onComplete?: () => void): void {
        for (const row of this.preMigrationRows) {
            row.status = 'running';
            row.errorMessage = undefined;
        }

        this.executionService
            .getFilteredMigrationLots(
                this.data.projectReleaseId,
                this.preMigrationRows.map(row => row.id)
            )
            .subscribe({
                next: response => {
                    const resultByLotId = this.buildPreMigrationLotResultMap(response);

                    for (const row of this.preMigrationRows) {
                        const result = resultByLotId.get(row.id);
                        if (!result) {
                            row.status = 'error';
                            row.errorMessage = 'Selected lot is no longer available for migration in this release stage.';
                            continue;
                        }

                        if (result.canBeMigrated) {
                            row.status = 'success';
                            row.errorMessage = undefined;
                            continue;
                        }

                        row.status = 'error';
                        row.errorMessage =
                            (result.migrationEligibilityReasons ?? []).join(' ') ||
                            'This lot no longer passes the pre-migration checks.';
                    }

                    onComplete?.();
                },
                error: (error: unknown) => {
                    const message = this.stringifyError(error);
                    for (const row of this.preMigrationRows) {
                        row.status = 'error';
                        row.errorMessage = message;
                    }
                }
            });
    }

    private buildPreMigrationLotResultMap(
        groups: IProjectReleaseLotMigrationSourceSelectionGroupDto[]
    ): Map<number, IProjectReleaseLotMigrationSourceSelectionLotDto> {
        const results = new Map<number, IProjectReleaseLotMigrationSourceSelectionLotDto>();

        for (const group of groups) {
            for (const row of group.lots) {
                if (row.lot) {
                    results.set(row.lot.id, row.lot);
                }

                for (const childLot of row.childLots) {
                    results.set(childLot.id, childLot);
                }
            }
        }

        return results;
    }

    private async startCancellationStep(): Promise<void> {
        for (const row of this.cancelRows) {
            if (row.status !== 'pending') {
                continue;
            }

            await this.executeCancellationRow(row);
        }

        if (this.allCancellationsSuccessful) {
            await this.startImportStep();
        }
    }

    private async executeCancellationRow(row: ILotCancellationPreviewRow): Promise<void> {
        row.status = 'running';
        row.errorMessage = undefined;

        try {
            const deletedIds = await firstValueFrom(this.executionService.cancelLot(row.lot.id));
            this.markCancelledLotsSuccessful(deletedIds, row.id);
        } catch (error: unknown) {
            row.status = 'error';
            row.errorMessage = this.stringifyError(error);
        }
    }

    private markCancelledLotsSuccessful(deletedIds: number[], fallbackLotId: number): void {
        const affectedLotIds =
            deletedIds.length > 0 ? new Set(deletedIds) : new Set([fallbackLotId]);

        for (const row of this.cancelRows) {
            if (affectedLotIds.has(row.id)) {
                row.status = 'success';
                row.errorMessage = undefined;
            }
        }
    }

    private async startImportStep(): Promise<void> {
        if (this.showImportStep) {
            return;
        }

        this.showImportStep = true;
        this.importStep.status = 'running';
        this.importStep.errorMessage = undefined;

        try {
            const result = await firstValueFrom(
                this.executionService.uploadFile(this.data.projectReleaseId, this.data.importFile)
            );

            if (result === false) {
                throw new Error('Unable to upload the generated lot import file.');
            }

            this.importStep.status = 'success';
            this.migrateHistory();
            await this.startDocumentStep();
        } catch (error: unknown) {
            this.importStep.status = 'error';
            this.importStep.errorMessage = this.stringifyError(error);
        }
    }

    private async resumeAfterSuccessfulImportRetry(): Promise<void> {
        this.importStep.status = 'success';
        this.importStep.errorMessage = undefined;
        this.migrateHistory();
        await this.startDocumentStep();
    }

    private async startDocumentStep(): Promise<void> {
        if (this.showDocumentStep) {
            return;
        }

        this.showDocumentStep = true;

        if (this.documentRows.length < 1) {
            this.documentStepStatus = 'skipped';
            return;
        }

        this.documentStepStatus = 'running';

        try {
            const lotIdByNumber = await firstValueFrom(
                this.executionService.getDestinationLotIdMap(this.data.projectReleaseId)
            );

            const documentRequests = this.buildDocumentMigrationRequests(lotIdByNumber);
            if (documentRequests.length > 0) {
                const migrateResponse = await firstValueFrom(
                    this.executionService.migrateDocuments(
                        documentRequests.map(request => request.assignment)
                    )
                );
                this.applyDocumentMigrationResults(documentRequests, migrateResponse);
            }
        } catch (error: unknown) {
            const message = this.stringifyError(error);
            for (const row of this.documentRows) {
                if (row.status === 'pending' || row.status === 'running') {
                    row.status = 'error';
                    row.errorMessage = message;
                }
            }
        }

        this.documentStepStatus = this.hasDocumentIssues ? 'error' : 'success';
    }

    private buildDocumentMigrationRequests(
        lotIdByNumber: Map<number, number>
    ): IDocumentMigrationRequest[] {
        const requests: IDocumentMigrationRequest[] = [];

        for (const row of this.documentRows) {
            row.status = 'running';
            row.errorMessage = undefined;

            const destinationLotId =
                row.destinationLotNumber == null
                    ? undefined
                    : lotIdByNumber.get(row.destinationLotNumber);
            if (!destinationLotId) {
                row.status = 'error';
                row.errorMessage = 'Unable to resolve the destination lot after the import completed.';
                continue;
            }

            requests.push({
                row,
                assignment: {
                    documentEntityId: row.assignment.documentEntityId,
                    destinationLotId
                }
            });
        }

        return requests;
    }

    private buildHistoryRequest(
        lotIdByNumber: Map<number, number>
    ): IMigrateLotsWizardHistoryRequestDto {
        const cancelledLotIds = this.data.lots.map(lot => lot.id);
        const createdLotIds = this.data.newLotSummaries
            .map(lot => lotIdByNumber.get(lot.lotNumber)!);
        const mappedLotIdsByCancelledLotId: Record<number, number> = {};

        for (const [cancelledLotId, createdLotNumber] of Object.entries(
            this.data.sourceToDestinationLotNumberMap
        )) {
            mappedLotIdsByCancelledLotId[Number(cancelledLotId)] =
                lotIdByNumber.get(createdLotNumber)!;
        }

        return {
            cancelledLotIds,
            createdLotIds,
            mappedLotIdsByCancelledLotId
        };
    }

    private migrateHistory(): void {
        this.executionService.getDestinationLotIdMap(this.data.projectReleaseId).pipe(take(1)).subscribe((resolvedLotIdByNumber) => {
            const request = this.buildHistoryRequest(resolvedLotIdByNumber);
            this.executionService.migrateHistory(request).pipe(take(1)).subscribe();
        });
    }

    private applyDocumentMigrationResults(
        requests: IDocumentMigrationRequest[],
        response: IMigrateLotsWizardDocumentMigrateResponseDto
    ): void {
        const resultByIndex = new Map<number, IMigrateLotsWizardDocumentMigrateResultDto>();

        for (const result of response.results ?? []) {
            resultByIndex.set(result.assignmentIndex, result);
        }

        for (let requestIndex = 0; requestIndex < requests.length; requestIndex++) {
            const request = requests[requestIndex];
            const result = resultByIndex.get(requestIndex);

            if (!result) {
                request.row.status = 'error';
                request.row.errorMessage = 'Document migration result was not returned.';
                continue;
            }

            if (result.isSuccess) {
                request.row.status = 'success';
                request.row.errorMessage = undefined;
                continue;
            }

            request.row.status = 'error';
            request.row.errorMessage =
                result.errorMessage || 'Document migration did not complete.';
        }
    }

    private stringifyError(error: unknown): string {
        return stringifyReadableError((error as any)?.error ?? error);
    }
}
